/**
 * THE COOKIE POLICY, WRITTEN ONCE (PV5a) — prest0.ai/cookie-policy, in today's design (src/pages/Cookie.tsx) and in the
 * new design's reading page (src/design/pavilion/pages/cookie-policy/). See ./legalDocument.ts for how a document is
 * shared.
 *
 * Moved here from Cookie.tsx as written, part names aside; the page's rendered HTML was compared byte for byte before
 * and after the move. Today's page drew its section titles with no ids; the ids below are new, for the new design's
 * contents, and today's page still draws its titles without them. The date it took effect is
 * src/config/legalVersions.ts's (EffectiveDate).
 */
import React from 'react';
import type { LegalDocument, LegalParts } from './legalDocument';

/** What the page says above the document: its title and the sentence under it. */
export const COOKIE_POLICY_HEAD = {
  heading: 'cookie policy',
  lead: 'how prest0 uses cookies and similar technologies to protect and personalize your experience',
  /** The contents the new design's reading page draws (today's page has none). */
  contentsTitle: 'on this page',
  contentsName: 'cookie policy table of contents',
} as const;

export type CookiePolicyParts = Pick<
  LegalParts,
  'Callout' | 'Cell' | 'Fact' | 'FactLabel' | 'FactValue' | 'Facts' | 'HeadCell' | 'List' | 'ListItem' | 'OrderedList' | 'OrderedListItem' | 'SubTitle' | 'Table' | 'Text'
>;

/** The cookie policy, in the parts the caller draws it with. */
export function cookiePolicyDocument({
  Callout, Cell, Fact, FactLabel, FactValue, Facts, HeadCell, List, ListItem, OrderedList, OrderedListItem, SubTitle, Table, Text,
}: CookiePolicyParts): LegalDocument {
  return {
    intro: (
      <>
        <Text>
          prest0 uses cookies and similar technologies to provide, maintain, and continually improve our services. This cookie policy explains what data we collect through these technologies, how we use it, and the options available to you.
        </Text>
        <Text>
          The way we set, store, and honor cookies lines up with the commitments on our security page: TLS 1.3 protects every session, data at rest is encrypted with AES-256 on Google Cloud, sign-in through Google or an email and password guards account access, prompts never train shared models (contractual no-training guarantee), and documented retention schedules govern storage lifecycles.
        </Text>

        <Facts>
          <Fact>
            <FactLabel>cookie scope</FactLabel>
            <FactValue>first-party (prest0.ai) only — we set no advertising or analytics cookies and load no third-party trackers</FactValue>
          </Fact>
          <Fact>
            <FactLabel>retention window</FactLabel>
            <FactValue>session-based, plus the sign-in token and the preferences your browser keeps for you</FactValue>
          </Fact>
          <Fact>
            <FactLabel>consent banner</FactLabel>
            <FactValue>none — there is nothing to consent to, because we set no non-essential cookies</FactValue>
          </Fact>
        </Facts>

        <Callout>
          you will not meet a cookie wall here, and that is not an oversight. prest0 runs no advertising or analytics trackers at all, so there is no non-essential cookie to accept or decline and nothing for a banner to ask you. the only cookies we set are the ones that keep you signed in and remember how you have set things up. your browser controls remain the full set of controls.
        </Callout>
      </>
    ),
    sections: [
      {
        id: 'security-controls',
        heading: 'how cookies align with our security controls',
        body: (
          <>
            <Text>
              Our security page outlines the baseline controls we run across every workspace. Those same safeguards govern how cookies and similar technologies work inside prest0:
            </Text>
            <List>
              <ListItem>TLS 1.3 keeps preference requests and authentication cookies encrypted in transit.</ListItem>
              <ListItem>Data stored in Google Cloud (including the preferences you set) is protected with AES-256 encryption and managed key rotation.</ListItem>
              <ListItem>Sign-in through Google or an email and password, and role-based permissions, guard access to admin tooling.</ListItem>
              <ListItem>The contractual no-training guarantee covers telemetry tied to cookies—prompts, transcripts, and analytics derived from them never fine-tune shared models.</ListItem>
              <ListItem>Log retention follows the same documented schedule as the rest of the platform. Self-service timers and automated deletion receipts are on the roadmap, not shipped.</ListItem>
              <ListItem>Append-only audit logs, and the SOC 2 TYPE II and ISO/IEC 27001 controls we inherit from Google Cloud, back the platform these preferences live on.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'what-are-cookies',
        heading: 'what are cookies?',
        body: (
          <>
            <Text>
              Cookies are small text files that websites place on your device to remember information about your visit. Some persist after you close your browser (persistent cookies) while others disappear when you end the session (session cookies). We rely on both types to keep the prest0 experience smooth, secure, and personalized.
            </Text>
          </>
        ),
      },
      {
        id: 'categories',
        heading: 'cookie categories we rely on',
        body: (
          <>
            <Text>
              We group cookies by how they support your workflow. The table below summarizes each category and how it shows up while you use prest0.
            </Text>

            <Table>
              <thead>
                <tr>
                  <HeadCell>category</HeadCell>
                  <HeadCell>how it supports your session</HeadCell>
                  <HeadCell>examples you may see</HeadCell>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Cell>essential</Cell>
                  <Cell>keeps you signed in, protects gated areas, remembers fundamental preferences, and routes traffic securely across our infrastructure.</Cell>
                  <Cell>session tokens for secure login, load balancer routing identifiers, consent state flags.</Cell>
                </tr>
                <tr>
                  <Cell>performance & analytics</Cell>
                  <Cell>none. we run no analytics product on this site — no Google Analytics, no Segment, no PostHog, no session replay, no A/B testing allocator.</Cell>
                  <Cell>nothing is set in this category.</Cell>
                </tr>
                <tr>
                  <Cell>functionality</Cell>
                  <Cell>remembers how you configure dashboards, the last agent you edited, and assists with faster reloads across sessions.</Cell>
                  <Cell>saved layout preferences, agent builder state markers, locale or time-zone selectors.</Cell>
                </tr>
                <tr>
                  <Cell>marketing</Cell>
                  <Cell>none. we set no advertising cookie and embed no social or ad-network pixel — no Meta, no LinkedIn, no X.</Cell>
                  <Cell>nothing is set in this category.</Cell>
                </tr>
              </tbody>
            </Table>
          </>
        ),
      },
      {
        id: 'technologies',
        heading: 'technologies we rely on',
        body: (
          <>
            <SubTitle>browser cookies</SubTitle>
            <Text>
              We set first-party cookies directly from prest0.ai to manage authentication, remember preferences, and stabilize navigation. Every cookie exchange rides over TLS 1.3, and the data is scoped to our domain so it inherits the same protections called out on the security page.
            </Text>
            <SubTitle>local storage & indexeddb</SubTitle>
            <Text>
              For real-time editing, drafts and layout we keep working state in local storage and IndexedDB. That data stays in your browser, is sent to our servers only over TLS 1.3, and is encrypted at rest on Google Cloud once it arrives. We do not encrypt it client-side before it leaves your browser, and we would rather say so than imply a protection we have not built.
            </Text>
            <SubTitle>server-side logging</SubTitle>
            <Text>
              Standard server logs capture IP address, browser metadata, and timestamps. Those logs sit on Google Cloud, inherit AES-256 encryption, roll up into append-only audit trails, and follow the documented retention schedule described in our security commitments.
            </Text>
          </>
        ),
      },
      {
        id: 'when-we-set-cookies',
        heading: 'when we set cookies',
        body: (
          <>
            <List>
              <ListItem>when you create or access your account to maintain a secure session.</ListItem>
              <ListItem>when you interact with the builder, dashboards, or monetization tools to remember your layout and selections.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'managing-preferences',
        heading: 'managing your preferences',
        body: (
          <>
            <Text>
              Most browsers allow you to control cookies through their settings. The exact location differs, but these steps cover the general flow:
            </Text>
            <OrderedList>
              <OrderedListItem>open your browser settings or preferences pane.</OrderedListItem>
              <OrderedListItem>look for privacy, security, or cookies controls.</OrderedListItem>
              <OrderedListItem>choose whether to block, clear, or allow cookies and site data.</OrderedListItem>
              <OrderedListItem>save your changes and refresh the prest0 tab.</OrderedListItem>
            </OrderedList>
            <Text>quick references for popular browsers:</Text>
            <List>
              <ListItem><strong>Google Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data.</ListItem>
              <ListItem><strong>Safari:</strong> Settings &gt; Safari &gt; Privacy & Security or Preferences &gt; Privacy on macOS.</ListItem>
              <ListItem><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Manage and delete cookies and site data.</ListItem>
              <ListItem><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and site data.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'analytics-and-third-parties',
        heading: 'analytics and third-party cookies',
        body: (
          <>
            <Text>
              There are none. prest0.ai loads no analytics product, no advertising network and no social pixel, so there is no third-party cookie for you to opt out of and no vendor holding a record of your visit. We do not sell or share personal information, and nothing on this site feeds a shared AI model.
            </Text>
            <Text>
              The only third-party requests the site makes are for the fonts it renders in and the Google Cloud infrastructure it is served from. If that ever changes, it changes here first, with a new effective date, before the code ships.
            </Text>
          </>
        ),
      },
      {
        id: 'retention',
        heading: 'retention and refresh cycles',
        body: (
          <>
            <Text>
              Session cookies last as long as your session. Your sign-in token lasts until it expires or you sign out. Preferences your browser holds for you — layout, locale, the last thing you were working on — stay until you clear your browser data. There are no analytics or marketing cookies to expire, because none are set.
            </Text>
          </>
        ),
      },
      {
        id: 'do-not-track',
        heading: 'do not track and global privacy control',
        body: (
          <>
            <Text>
              If your browser sends a Global Privacy Control or Do Not Track signal, we honor it — and in our case honoring it changes nothing, because we were never selling or sharing your personal information, running cross-site advertising, or setting a tracker for anyone to read. There is no state in which this site behaves differently depending on that header.
            </Text>
          </>
        ),
      },
      {
        id: 'policy-updates',
        heading: 'updates to this policy',
        body: (
          <>
            <Text>
              We review this policy at least annually and whenever we introduce significant product changes or new third-party partnerships. Updates become effective once they appear on this page with a refreshed date stamp.
            </Text>
          </>
        ),
      },
    ],
  };
}
